import type { HlsReadinessMetadata } from './HlsReadiness';
import type { ContextualVisualAugmentStep, ContextualVisualGuide, ContextualVisualRequestContext } from './ContextualVisual';
import type { FootballPostSnapRead, FootballPreSnapRead } from './FootballCoachRead';

export type VisionSourceKind =
  | 'screen_share'
  | 'gameplay_capture'
  | 'participant_video';

export type ScreenShareSourceMode =
  | 'screen'
  | 'window'
  | 'browser_tab'
  | 'gameplay';

export type DesktopAvCaptureDisplaySurface =
  | 'application'
  | 'browser'
  | 'monitor'
  | 'unknown'
  | 'window';

export type ParticipantVideoMode =
  | 'self'
  | 'active_speaker'
  | 'selected_participant';

export type VisionSourcePolicyMode =
  | 'room_policy'
  | 'host_enabled'
  | 'participant_enabled'
  | 'not_allowed';

export type VisionSourcePrivacy =
  | 'public_room_surface'
  | 'participant_camera_policy'
  | 'participant_private_opt_in';

export interface VisionFrameSource {
  sourceId: string;
  kind: VisionSourceKind;
  roomId: string;
  participantId: string;
  label?: string;
  screenShareMode?: ScreenShareSourceMode;
  participantVideoMode?: ParticipantVideoMode;
  policyMode: VisionSourcePolicyMode;
  /**
   * @deprecated Use policyMode. Kept for compatibility with the first gameplay
   * vision spike naming.
   */
  consentMode?: VisionSourcePolicyMode;
  priority: number;
}

/**
 * Timing of a browser frame before encoding or inference. This clock belongs to
 * the local sampler, not to published RTP or a viewer's presentation timeline.
 * A sourceEpoch changes when the sampler binds a new track or restarts.
 */
export interface VisionCaptureEvidence {
  sourceEpoch: string;
  clockId: string;
  clockDomain: 'browser_video_element';
  frameId: string;
  frameSequence: number;
  capturedAtMs: number;
  captureMonotonicMs: number;
  captureDurationMs: number;
  /** Local HTMLVideoElement position only; null if the browser has none. */
  mediaTimeMs: number | null;
  /** Null until the uncertainty relative to published media can be bounded. */
  uncertaintyMs: number | null;
}

export interface ParticipantCameraState {
  roomId: string;
  ownerSessionId: string;
  membershipId: string;
  /** Compare-and-set sequence for control updates, separate from the capture incarnation. */
  revision: number;
  enabled: boolean;
  sourceIncarnation: string | null;
  /** Remaining server lease; clients do not compare wall clocks. */
  leaseRemainingMs: number;
}
export type ParticipantCameraRequest =
  | {operation:'read';roomId:string;ownerSessionId?:string}
  | {operation:'update';roomId:string;membershipId:string;expectedRevision:number;bindingId:string|null};
export type ParticipantCameraReply = {ok:true;state:ParticipantCameraState}
  | {ok:false;reason:'rejected'|'conflict'|'unavailable';state?:ParticipantCameraState};

export type VisionAccessCategory = 'shared' | 'stage' | 'participant';
export interface VisualAccessToken {
  sessionId: string;
  category: VisionAccessCategory;
  sourceId: string;
  revision: number;
  sourceIncarnation?: string;
  /** Canonical room membership and connection that owned this participant surface. */
  ownerSessionId?: string;
  ownerConnectionId?: string;
  ownerMembershipId?: string;
}
export function parseVisualAccessToken(value: unknown): VisualAccessToken | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const token=value as Partial<VisualAccessToken>;
  if (typeof token.sessionId !== 'string' || !token.sessionId || token.sessionId.length>240
    || typeof token.sourceId !== 'string' || !token.sourceId || token.sourceId.length>240
    || !['shared','stage','participant'].includes(token.category ?? '')
    || !Number.isSafeInteger(token.revision) || token.revision!<0) return undefined;
  if (token.sourceIncarnation !== undefined && (typeof token.sourceIncarnation !== 'string' || !token.sourceIncarnation || token.sourceIncarnation.length > 240)) return undefined;
  for (const value of [token.ownerSessionId, token.ownerConnectionId, token.ownerMembershipId]) {
    if (value !== undefined && (typeof value !== 'string' || !value || value.length > 240)) return undefined;
  }
  return {sessionId:token.sessionId,sourceId:token.sourceId,category:token.category!,revision:token.revision!,
    ...(token.ownerSessionId ? {ownerSessionId:token.ownerSessionId} : {}),
    ...(token.ownerConnectionId ? {ownerConnectionId:token.ownerConnectionId} : {}),
    ...(token.ownerMembershipId ? {ownerMembershipId:token.ownerMembershipId} : {}),
    ...(token.sourceIncarnation ? {sourceIncarnation:token.sourceIncarnation} : {})};
}
export interface VisualAccessState {
  roomId: string;
  sessionId: string;
  policies: Record<VisionAccessCategory, {enabled:boolean;revision:number;source:{sourceId:string;incarnation:string;ownerSessionId?:string}|null}>;
  /** Bounded projection of currently relevant members, read with policy in one transaction. */
  owners?: Record<string, {connectionId:string;membershipId:string;camera?:{sourceIncarnation:string}}>;
}
export function visualAccessCategory(source: {sourceKind?: VisionSourceKind;sourceId:string}): VisionAccessCategory {
  return source.sourceKind === 'participant_video' ? 'participant' : source.sourceId.startsWith('stage-media:') ? 'stage' : 'shared';
}
export function visualAccessFor(state: VisualAccessState | null, source: {roomId:string;sourceId:string;sourceKind?:VisionSourceKind;ownerSessionId?:string;sourceIncarnation?:string}): VisualAccessToken | null {
  if (!state || state.roomId !== source.roomId) return null;
  const category=visualAccessCategory(source), policy=state.policies[category];
  if (!policy?.enabled || !Number.isSafeInteger(policy.revision)) return null;
  if (category === 'participant') {
    if (!source.ownerSessionId || source.sourceId !== `participant-camera-${source.ownerSessionId}`) return null;
  } else if (policy.source?.sourceId !== source.sourceId || !source.sourceIncarnation || policy.source.incarnation !== source.sourceIncarnation) return null;
  const ownerSessionId = category === 'participant' ? source.ownerSessionId : policy.source?.ownerSessionId;
  const owner = ownerSessionId ? state.owners?.[ownerSessionId] : undefined;
  if (category !== 'stage' && (!ownerSessionId || !owner?.connectionId || !owner?.membershipId
    || source.ownerSessionId && source.ownerSessionId !== ownerSessionId)) return null;
  if (category === 'participant' && (!source.sourceIncarnation || owner?.camera?.sourceIncarnation !== source.sourceIncarnation)) return null;
  return {sessionId:state.sessionId,category,sourceId:source.sourceId,revision:policy.revision,
    ...(category !== 'stage' ? {ownerSessionId,ownerConnectionId:owner!.connectionId,ownerMembershipId:owner!.membershipId} : {}),
    sourceIncarnation:source.sourceIncarnation};
}
export function visualAccessMatches(observation: Pick<VisualObservation,'roomId'|'sourceId'|'sourceKind'|'ownerSessionId'|'access'>, state: VisualAccessState | null): boolean {
  const actual=observation.access;
  const expected=visualAccessFor(state,{...observation,sourceIncarnation:actual?.sourceIncarnation});
  return !!expected && !!actual && expected.sessionId===actual.sessionId && expected.category===actual.category
    && expected.sourceId===actual.sourceId && expected.revision===actual.revision
    && expected.ownerSessionId===actual.ownerSessionId && expected.ownerConnectionId===actual.ownerConnectionId && expected.ownerMembershipId===actual.ownerMembershipId && expected.sourceIncarnation===actual.sourceIncarnation;
}

export interface VisualObservation {
  /** Optional, request-scoped drawing. Not ambient scene authority. */
  visualGuide?: ContextualVisualGuide;
  /** #5057 — Augment: spoken steps over the guide's marks, in order. Request-scoped like the guide. */
  augmentSteps?: ContextualVisualAugmentStep[];
  /** #5083 — a football coach read: both play-type calls and the marked players. Request-scoped like the guide. */
  footballRead?: FootballPreSnapRead;
  /** #5083 — the post-snap step: both calls revised, and the players it could not follow. */
  footballPostSnap?: FootballPostSnapRead;
  /** Server-issued room session and policy revision, never trusted from upload JSON. */
  access?: VisualAccessToken;
  roomId: string;
  sourceId: string;
  sourceKind?: VisionSourceKind;
  ownerSessionId?: string;
  ownerAlias?: string;
  label?: string;
  /** Original valid capture wall time, or 0 when it cannot establish timing. */
  observedAt: number;
  capture?: VisionCaptureEvidence;
  frameHash?: string;
  frameCount?: number;
  receivedAt?: number;
  inferenceCompletedAt?: number;
  /** Valid capture wall time does not establish a Sixync media-clock mapping. */
  captureTimestampValid?: boolean;
  expiresAt: number;
  summary: string;
  /** Provider failure retained for consumers; failed replies are never evidence. */
  failureReason?: string;
  visibleText?: string[];
  action?: string;
  mood?: string;
  confidence: number;
  provider: 'openai' | 'google' | 'anthropic' | 'xai' | 'local';
  model: string;
  costClass: 'cheap_snapshot' | 'realtime_burst';
  sourcePrivacy: VisionSourcePrivacy;
  /**
   * #3623 — which known prompt produced this reading, when it was one.
   *
   * The request has carried this since #3615; the stored observation did not,
   * so nothing downstream of the model call could tell a scoreboard reading
   * from an ambient one — including the writer that has to decide which key it
   * belongs under.
   */
  promptReason?: VisionPromptReason;
  /**
   * Structured facts read from a televised sports-stat graphic in this exact
   * frame. These are observations, not authoritative provider facts; consumers
   * must preserve the printed scope and attribute answers to the broadcast.
   */
  broadcastStats?: BroadcastStatObservation[];
}

export type BroadcastStatScope = 'current_game' | 'season' | 'career' | 'other' | 'unknown';

export type BroadcastStatKey =
  | 'passing_yards'
  | 'rushing_yards'
  | 'receiving_yards'
  | 'completions'
  | 'attempts'
  | 'passing_touchdowns'
  | 'interceptions'
  | 'carries'
  | 'receptions'
  | 'tackles'
  | 'sacks'
  | 'other';

/** One literal player-stat value printed by the broadcast. */
export interface BroadcastStatObservation {
  playerName: string;
  team: string | null;
  role: string | null;
  statKey: BroadcastStatKey;
  statLabel: string;
  value: string;
  scope: BroadcastStatScope;
  overlayTitle: string | null;
  period: number | null;
  clock: string | null;
  confidence: number;
}

export interface BroadcastStatFact extends BroadcastStatObservation {
  sourceId: string;
  sourceEpoch?: string;
  frameHash?: string;
  observedAt: number;
  lastSeenAt: number;
  observationCount: number;
}

/** Temporary, source-scoped notebook for the active room share. */
export interface BroadcastStatNotebook {
  access?: VisualAccessToken;
  roomId: string;
  sourceId: string;
  sourceEpoch?: string;
  updatedAt: number;
  activeUntil: number;
  expiresAt: number;
  facts: BroadcastStatFact[];
}

/**
 * #3623 — where a source's latest reading lives.
 *
 * Defined once and imported by both writer and reader. The signaling side used
 * to re-derive this string from a comment that said it "mirrors the route's own
 * encoding", which is a contract two files can drift out of silently.
 */
export function visionSourceLatestKey(roomId: string, sourceId: string): string {
  return `room:${roomId}:vision:source:${encodeURIComponent(sourceId).slice(0, 240)}:latest`;
}

/**
 * #3623 — where a SCOREBOARD reading lives, separately.
 *
 * Ambient sampling runs every eight seconds and wrote the same key as the
 * scoreboard burst, so a good scoreboard reading survived a few seconds at
 * most before a prose summary replaced it. Everything after that failed to
 * parse and silenced the room — 550 silences against 4 successful reads in one
 * sitting, all four inside the first six seconds of the watch-along.
 *
 * A separate key rather than a merged record: the ambient reading is still
 * wanted by every other consumer, and the two answer different questions.
 */
export function visionScoreboardLatestKey(roomId: string, sourceId: string): string {
  return `room:${roomId}:vision:source:${encodeURIComponent(sourceId).slice(0, 240)}:scoreboard`;
}

/** Room-local notebook of structured broadcast stat readings. */
export function visionBroadcastStatsKey(roomId: string): string {
  return `room:${roomId}:vision:cfb:broadcast-stats`;
}

export type VisionCaptureMode = 'ambient' | 'heartbeat' | 'burst';

export interface VisionBurstRequestPayload {
  /** Server-created job identity; never a client-supplied instruction or authority grant. */
  inspectionId?: string;
  roomId: string;
  sourceId?: string;
  sourceKind?: VisionSourceKind;
  requestedBySessionId?: string;
  requestedByAlias?: string;
  requestedAt: number;
  durationMs?: number;
  /**
   * Why the burst was asked for.
   *
   * The first four are human-initiated. A `VisionPromptReason` additionally
   * names a prompt the CLIENT holds — see `visionPromptForReason` — which is how
   * a prompt longer than this event's 160-character `prompt` clamp reaches the
   * sampler at all. Widened here because signaling emits these itself; without
   * it a server-side requester cannot name a registered prompt.
   */
  reason: 'visual_question' | 'manual' | 'recording_marker' | 'system' | VisionPromptReason;
  prompt?: string;
  /**
   * #5083 — a football coach read. `presnap`: the sharer pins the frame it uploads and keeps recent frames of the play;
   * `postsnap`: it sends the pinned frame and frames of the play with the paused frame.
   */
  football?: 'presnap' | 'postsnap';
}

export interface VisualMomentMarker {
  access?: VisualAccessToken;
  roomId: string;
  momentId: string;
  sourceId: string;
  sourceKind?: VisionSourceKind;
  ownerSessionId?: string;
  ownerAlias?: string;
  label?: string;
  observedAt: number;
  createdAt: number;
  summary: string;
  visibleText?: string[];
  action?: string;
  confidence: number;
  captureMode: VisionCaptureMode;
  costClass: VisualObservation['costClass'];
  sourcePrivacy: VisionSourcePrivacy;
  recordingSourceId?: string;
  recordingStartedAt?: number;
  triggerPrompt?: string;
  triggeredBySessionId?: string;
  triggeredByAlias?: string;
}

export type RoomRecordingSourceKind =
  | 'gameplay_share'
  | 'program_stage'
  | 'room_stage'
  | 'screen_share';

export type RoomRecordingState =
  | 'idle'
  | 'recording'
  | 'buffering'
  | 'saving'
  | 'available'
  | 'error';

export interface RoomRecordingStatusPayload {
  roomId: string;
  recordingId: string;
  sourceKind: RoomRecordingSourceKind;
  sourceId?: string;
  ownerSessionId?: string;
  ownerAlias?: string;
  state: RoomRecordingState;
  startedAt?: number;
  stoppedAt?: number;
  durationMs?: number;
  estimatedSizeMB?: number;
  artifactName?: string;
  artifactUrl?: string;
  localOnly?: boolean;
  error?: string | null;
  timestamp: number;
}

export interface RoomRecordingCommandPayload {
  roomId: string;
  recordingId: string;
  sourceKind: RoomRecordingSourceKind;
  sourceId?: string;
  action: 'start' | 'stop' | 'save_marker' | 'save_buffer';
  requestedBySessionId?: string;
  requestedByAlias?: string;
  timestamp: number;
}

export interface EncodedVisionFrame {
  dataUrl: string;
  mimeType: 'image/jpeg' | 'image/webp';
  width: number;
  height: number;
  bytes: number;
  hash: string;
}

export interface VisionSnapshotRequest {
  /** Server-owned inspection policy; ignored when parsing client uploads. */
  contextualVisual?: ContextualVisualRequestContext;
  inspectionId?: string;
  /** Canonical source identity bound when the capture stream is selected; independent of local capture.sourceEpoch. */
  sourceIncarnation?: string;
  roomId: string;
  sourceId: string;
  sourceKind?: VisionSourceKind;
  ownerSessionId: string;
  ownerAlias?: string;
  label?: string;
  joinToken?: string;
  capturedAt: number;
  capture?: VisionCaptureEvidence;
  mode: VisionCaptureMode;
  sourcePrivacy?: VisionSourcePrivacy;
  /** #5064 — how many pen marks (anyone in the room may draw) are drawn into this frame (shared screens only). */
  hostInkMarks?: number;
  screenShareMode?: ScreenShareSourceMode;
  participantVideoMode?: ParticipantVideoMode;
  policyMode?: VisionSourcePolicyMode;
  triggerPrompt?: string;
  /**
   * #3615 — which known prompt this burst is running, when it is one.
   *
   * Carried alongside `triggerPrompt` rather than inferred from it. The route
   * has to pick a RESPONSE SCHEMA, and sniffing prompt text for that is a
   * guess that gets quietly wrong the first time the prompt is reworded. The
   * reason is already what the client resolved the prompt from, so passing it
   * through costs nothing and says exactly what was asked for.
   */
  promptReason?: VisionPromptReason;
  triggeredBySessionId?: string;
  triggeredByAlias?: string;
  frameCount: number;
  image: EncodedVisionFrame;
  /**
   * #5083 — a post-snap coach read: earlier frames of the play, oldest first (the pinned pre-snap frame, then frames
   * of the play), before `image`, the paused frame. At most FOOTBALL_SEQUENCE_MAX_FRAMES.
   */
  sequence?: Array<{ capturedAt: number; image: EncodedVisionFrame }>;
  /** #5083 — when the sharer saw the play start (the first real change after the pinned pre-snap frame). */
  snapAt?: number;
}

export interface VisionSnapshotTelemetry {
  provider: VisualObservation['provider'];
  model: string;
  latencyMs: number;
  imageBytes: number;
  frameCount: number;
  mode: VisionCaptureMode;
  stored: boolean;
}

export interface VisionSnapshotResponse {
  observation: VisualObservation;
  telemetry: VisionSnapshotTelemetry;
  moment?: VisualMomentMarker | null;
}

export interface GameplayVisionSnapshotRequest extends VisionSnapshotRequest {}
export type GameplayVisionSnapshotTelemetry = VisionSnapshotTelemetry;
export type GameplayVisionSnapshotResponse = VisionSnapshotResponse;

export const GAMEPLAY_SHARE_MODULE_ID = 'gameplay-share' as const;

export type GameplayShareDistribution = 'media_server' | 'p2p';

export type DesktopAvSourceKind =
  | 'desktop_av'
  | 'gameplay'
  | 'screen_share';

export type DesktopAvTransport =
  | 'media_server'
  | 'external';

export type DesktopAvProgramContributionPolicy =
  | 'allowed'
  | 'excluded'
  | 'host_only';

export type GameplayShareRecordingSourceKind = Extract<
  RoomRecordingSourceKind,
  'gameplay_share' | 'screen_share'
>;

export interface GameplaySharePlaybackEndpoints extends HlsReadinessMetadata {
  transport: GameplayShareDistribution;
  sourceKind: GameplayShareRecordingSourceKind;
  streamPath: string;
  moqWt?: string | null;
  moqWtAac?: string | null;
  hls?: string | null;
}

/**
 * What the browser actually handed back for a share's audio (#3752).
 *
 * `windowAudio` and `restrictOwnAudio` are REQUESTS. Whether Chrome honoured
 * them decides whether the room's own output — companion TTS, remote voices —
 * is inside the share, and until this shape shipped nothing outside the
 * sharer's console could tell. Read from `track.getSettings()` after the
 * capture constraints are applied. `hasAudioTrack: false` means the pick
 * carried no audio at all.
 */
export interface ShareAudioCaptureSettings {
  hasAudioTrack: boolean;
  /** The audio track's own displaySurface setting, when the browser reports one. */
  displaySurface?: DesktopAvCaptureDisplaySurface;
  /** `true` only when the browser echoes the constraint back as applied. */
  restrictOwnAudio?: boolean;
  /** Echo of the `windowAudio` option, where the browser exposes it. */
  windowAudio?: 'exclude' | 'window' | 'system';
  /** Track label, e.g. "window:1116536:3" — it does NOT distinguish a Chrome window from a native one. */
  label?: string;
}

/**
 * Can this share's audio contain the room's own output? (#3767)
 *
 * `isolated`     — the surface cannot carry room output (a tab; a pick with no audio)
 * `filtered`     — it could, but the browser echoed `restrictOwnAudio: true`
 * `contaminated` — the room heard its own voice through it (#3754), or a
 *                  monitor pick where own-audio was not restricted
 * `unknown`      — a window pick: a Chrome window and a native app window
 *                  look the same until the room hears itself
 */
export type ShareAudioIsolation = 'isolated' | 'filtered' | 'contaminated' | 'unknown';

export interface ShareAudioIsolationState {
  sourceId: string;
  verdict: ShareAudioIsolation;
  reason: string;
  updatedAt: number;
}

export interface ShareAudioIsolationChangedPayload extends ShareAudioIsolationState {
  roomId: string;
}

/** #3770 — a client-measured verdict, from the sharer's acoustic detector. */
export interface ShareAudioIsolationReportPayload {
  roomId: string;
  sourceId: string;
  verdict: Extract<ShareAudioIsolation, 'contaminated' | 'isolated'>;
  reason: string;
  correlation: number;
  delayMs: number;
}

export interface GameplayShareStatePayload {
  roomId: string;
  sourceId: string;
  ownerSessionId: string;
  ownerAlias: string;
  label?: string;
  screenShareMode: ScreenShareSourceMode;
  startedAt: number;
  visionSamplingActive?: boolean;
  transport?: GameplayShareDistribution;
  recordingSourceKind?: GameplayShareRecordingSourceKind;
  streamPath?: string;
  rawStreamPath?: string;
  desktopAvSourceKind?: DesktopAvSourceKind;
  captureDisplaySurface?: DesktopAvCaptureDisplaySurface;
  shareAudioCapture?: ShareAudioCaptureSettings;
  shareAudioIsolation?: ShareAudioIsolation;
  programContributionPolicy?: DesktopAvProgramContributionPolicy;
  playback?: GameplaySharePlaybackEndpoints | null;
}

export interface GameplayShareStopPayload {
  roomId: string;
  sourceId: string;
  ownerSessionId: string;
  stoppedAt: number;
  reason?: 'user_stopped' | 'track_ended' | 'host_left' | 'error';
}

export interface DesktopAvPlaybackEndpoints extends HlsReadinessMetadata {
  streamPath: string;
  moqWt?: string | null;
  moqWtAac?: string | null;
  hls?: string | null;
}

export interface DesktopAvSourceStartedPayload {
  roomId: string;
  sourceId: string;
  sourceKind: DesktopAvSourceKind;
  ownerSessionId: string;
  ownerAlias?: string;
  label?: string;
  transport: DesktopAvTransport;
  rawStreamPath: string;
  playback: DesktopAvPlaybackEndpoints;
  recordingSourceKind?: GameplayShareRecordingSourceKind;
  screenShareMode?: ScreenShareSourceMode;
  captureDisplaySurface?: DesktopAvCaptureDisplaySurface;
  shareAudioCapture?: ShareAudioCaptureSettings;
  shareAudioIsolation?: ShareAudioIsolation;
  visionSamplingActive?: boolean;
  programContributionPolicy: DesktopAvProgramContributionPolicy;
  startedAt: number;
}

export interface DesktopAvSourceStoppedPayload {
  roomId: string;
  sourceId: string;
  ownerSessionId: string;
  stoppedAt: number;
  reason?: GameplayShareStopPayload['reason'];
}

export function getDesktopAvSourceKindForScreenShareMode(
  mode: ScreenShareSourceMode | null | undefined,
): DesktopAvSourceKind {
  return mode === 'gameplay' ? 'gameplay' : 'screen_share';
}

export function getGameplayShareRecordingSourceKindForDesktopAvSource(
  sourceKind: DesktopAvSourceKind,
): GameplayShareRecordingSourceKind {
  return sourceKind === 'gameplay' ? 'gameplay_share' : 'screen_share';
}

export function toDesktopAvSourceStartedPayload(
  payload: GameplayShareStatePayload,
): DesktopAvSourceStartedPayload {
  const sourceKind = payload.desktopAvSourceKind ??
    getDesktopAvSourceKindForScreenShareMode(payload.screenShareMode);
  const recordingSourceKind =
    payload.recordingSourceKind ??
    getGameplayShareRecordingSourceKindForDesktopAvSource(sourceKind);
  const rawStreamPath =
    payload.rawStreamPath ??
    payload.streamPath ??
    payload.playback?.streamPath ??
    '';
  const playback: DesktopAvPlaybackEndpoints = {
    streamPath: payload.playback?.streamPath ?? rawStreamPath,
    moqWt: payload.playback?.moqWt ?? null,
    moqWtAac: payload.playback?.moqWtAac ?? null,
    hls: payload.playback?.hls ?? null,
    hlsReady: payload.playback?.hlsReady,
    hlsStatus: payload.playback?.hlsStatus,
  };

  return {
    roomId: payload.roomId,
    sourceId: payload.sourceId,
    sourceKind,
    ownerSessionId: payload.ownerSessionId,
    ownerAlias: payload.ownerAlias,
    label: payload.label,
    transport: payload.transport === 'p2p' ? 'external' : 'media_server',
    rawStreamPath,
    playback,
    recordingSourceKind,
    screenShareMode: payload.screenShareMode,
    captureDisplaySurface: payload.captureDisplaySurface,
    shareAudioCapture: payload.shareAudioCapture,
    shareAudioIsolation: payload.shareAudioIsolation,
    visionSamplingActive: payload.visionSamplingActive,
    programContributionPolicy: payload.programContributionPolicy ?? 'allowed',
    startedAt: payload.startedAt,
  };
}

export function toGameplayShareStatePayload(
  payload: DesktopAvSourceStartedPayload,
): GameplayShareStatePayload {
  const recordingSourceKind =
    payload.recordingSourceKind ??
    getGameplayShareRecordingSourceKindForDesktopAvSource(payload.sourceKind);
  const screenShareMode =
    payload.screenShareMode ??
    (payload.sourceKind === 'gameplay' ? 'gameplay' : 'screen');
  const streamPath = payload.rawStreamPath || payload.playback.streamPath;

  return {
    roomId: payload.roomId,
    sourceId: payload.sourceId,
    ownerSessionId: payload.ownerSessionId,
    ownerAlias: payload.ownerAlias ?? '',
    label: payload.label,
    screenShareMode,
    startedAt: payload.startedAt,
    visionSamplingActive: payload.visionSamplingActive,
    transport: payload.transport === 'media_server' ? 'media_server' : 'p2p',
    recordingSourceKind,
    streamPath,
    rawStreamPath: streamPath,
    desktopAvSourceKind: payload.sourceKind,
    captureDisplaySurface: payload.captureDisplaySurface,
    shareAudioCapture: payload.shareAudioCapture,
    shareAudioIsolation: payload.shareAudioIsolation,
    programContributionPolicy: payload.programContributionPolicy,
    playback: {
      transport: payload.transport === 'media_server' ? 'media_server' : 'p2p',
      sourceKind: recordingSourceKind,
      streamPath: payload.playback.streamPath || streamPath,
      moqWt: payload.playback.moqWt ?? null,
      moqWtAac: payload.playback.moqWtAac ?? null,
      hls: payload.playback.hls ?? null,
      hlsReady: payload.playback.hlsReady,
      hlsStatus: payload.playback.hlsStatus,
    },
  };
}

export function toDesktopAvSourceStoppedPayload(
  payload: GameplayShareStopPayload,
): DesktopAvSourceStoppedPayload {
  return {
    roomId: payload.roomId,
    sourceId: payload.sourceId,
    ownerSessionId: payload.ownerSessionId,
    stoppedAt: payload.stoppedAt,
    reason: payload.reason,
  };
}

export function toGameplayShareStopPayload(
  payload: DesktopAvSourceStoppedPayload,
): GameplayShareStopPayload {
  return {
    roomId: payload.roomId,
    sourceId: payload.sourceId,
    ownerSessionId: payload.ownerSessionId,
    stoppedAt: payload.stoppedAt,
    reason: payload.reason,
  };
}

export interface RoomVisionSamplingStatePayload {
  roomId: string;
  enabled: boolean;
  updatedAt: number;
  updatedBySessionId?: string;
  updatedByAlias?: string;
}

/**
 * #2874 — the room's companion-awareness toggles, served to a socket on join.
 *
 * Held in memory on signaling and deleted when the room is torn down
 * (`cleanupConvoMgr`), so the state dies with the room and is never persisted.
 * That is what makes "session-scoped" true rather than merely intended: a room
 * can never be left in a listening state for whoever opens it next, and a client
 * that arrives mid-session no longer disagrees with the room it just joined.
 */
export interface CompanionAwarenessStatePayload {
  roomId: string;
  /** #2861 — companion replies with no PTT and no wake phrase. */
  autoRespond: boolean;
  /** #2863 — desktop/gameplay share audio becomes companion context. */
  shareAudioTranscription: boolean;
  /** #2865 — frames sampled from IPTV/HLS or MP4 stage media. */
  stageMediaVision: boolean;
  /** #3767 — the active share's audio-isolation verdict, when the room has caught one. */
  shareAudioIsolation?: ShareAudioIsolationState;
  updatedAt: number;
}

// ---------------------------------------------------------------------------
// #3135 — trigger-prompt registry for vision bursts.
//
// A burst carries a `reason`, and the CLIENT resolves that reason to a prompt it
// already holds. The prompt is deliberately NOT sent over the wire:
//
//   1. `ROOM_VISION_BURST_REQUESTED` clamps `prompt` to 160 characters
//      (server.ts). The scoreboard prompt is ~900, and the part that would be
//      truncated away is precisely the "report absence, do NOT guess"
//      instruction that makes the reading trustworthy.
//   2. Relaying a long prompt to every client on every request is waste, and it
//      version-skews: the prompt should ship with the code that parses its
//      output, not travel as data.
//
// Lives in `@telegamez/types` rather than `@telegamez/cfb` because the consumer
// is a browser component, and `@telegamez/cfb`'s entry point reaches `node:fs`
// through its artifact loader — importing it into a client bundle breaks the
// build. This file is already a web-next dependency and has no runtime imports.
// ---------------------------------------------------------------------------

/** Known burst reasons that resolve to a specific trigger prompt. */
export const VISION_PROMPT_REASONS = ['cfb_scoreboard'] as const;
export type VisionPromptReason = (typeof VISION_PROMPT_REASONS)[number];

/**
 * Read the on-screen score bug of a college football broadcast.
 *
 * Asks for the scoreboard graphic and nothing else — everything downstream is
 * correlation against a data feed, so a richer description costs tokens and
 * latency for fields nobody reads.
 *
 * The two load-bearing instructions:
 *
 * **Report absence explicitly.** A model asked to read a score will invent one
 * rather than admit the overlay is hidden during a replay or an advert. An
 * invented score correlates to a random moment and moves the stream offset to a
 * confident wrong value — worse than having no reading at all.
 *
 * **Do not infer.** The tell for a frame that is not the live game is that the
 * score bug is missing, and a model reasoning "this looks like football,
 * probably 21-17" destroys exactly that signal.
 */
export const VISION_SCOREBOARD_PROMPT = [
  'This frame may show a live American college football broadcast.',
  'Read ONLY the PRIMARY live-game score bug. Ignore scrolling tickers, other-game scores, schedules, replay graphics and studio panels. Do not infer anything from the play itself.',
  'Report, as JSON: {"teamScores":[{"team":string,"score":number|null},{"team":string,"score":number|null}]|null,"possession":string|null,"clock":"mm:ss"|null,"period":number|null,"down":number|null,"distance":number|"goal"|null,"scoreBugVisible":boolean}',
  'Read down (1–4) and yards to gain from the visible scoreboard only. For 2nd & Goal, report down=2 and distance="goal". Use null when absent or unreadable; never infer fields from a previous play.',
  'teamScores: pair each team label exactly as printed with the score immediately associated with it. Never assign home or away based on graphic order. If the association is unclear, use null.',
  'possession: the printed team label next to the possession marker — the small arrow, triangle, dot or highlighted team block on the bug. Report it whenever the marker is visible; null only if there is no marker.',
  'clock: the game clock exactly as printed, always with a leading digit before the colon — "0:38" or "12:44", never ":38".',
  'period: the quarter as a number, 1-4, or 5+ for overtime.',
  'If the score bug is not visible — a replay, an advert, a studio shot, a paused or non-football frame —',
  'set scoreBugVisible to false and every other field to null. Do NOT guess a score, a clock or a team.',
  'A wrong reading is far worse than a null one.',
].join('\n');

const VISION_PROMPTS: Record<VisionPromptReason, string> = {
  cfb_scoreboard: VISION_SCOREBOARD_PROMPT,
};

/**
 * #3615 — how much trigger prompt the snapshot route must accept.
 *
 * The scoreboard prompt is 748 characters and its `Report, as JSON: {...}`
 * line begins at character 164. The route clamped the field at 160, so that
 * instruction — the whole reason the prompt exists — was cut off four
 * characters before it started, on every single burst.
 *
 * The client resolves the prompt here precisely to dodge the SOCKET's 160-char
 * clamp; re-applying the same limit at the HTTP boundary defeated that. This
 * constant exists so the two cannot drift apart again: it is a bound with
 * headroom over the longest prompt in `VISION_PROMPTS`, not a number picked to
 * look generous.
 */
export const VISION_TRIGGER_PROMPT_MAX_CHARS = 1200;

/**
 * Resolve a burst reason to its trigger prompt.
 *
 * Returns undefined for any unknown reason, so an ordinary burst keeps whatever
 * prompt it supplied and a typo cannot silently swap one prompt for another.
 */
export function visionPromptForReason(reason: string | undefined): string | undefined {
  if (!reason) return undefined;
  return VISION_PROMPTS[reason as VisionPromptReason];
}

/**
 * #3910 — vision sampling cadence, and the TTL that must cover it.
 *
 * These live together, in shared types, because the bug they fix was these two
 * numbers drifting apart in two different packages. The client sampler decided
 * how often a frame is uploaded; the server route decided how long the result
 * survives; nothing tied them together, and the result was a companion that had
 * no camera context 89% of a real session.
 *
 * **The invariant: an observation must outlive the interval at which its source
 * re-uploads.** `visionObservationTtlMs` derives the TTL from the heartbeat
 * floor rather than restating it, so the two cannot separate again.
 *
 * A frame is only re-uploaded when its perceptual hash moves by at least the
 * scene-change distance; otherwise it waits for the heartbeat floor. That floor
 * is the worst case, so it is what the TTL has to cover.
 */
export const VISION_HEARTBEAT_FLOOR_MS = 5 * 60 * 1000;

/**
 * Margin over the heartbeat floor: inference latency plus a scheduling tick.
 * `expiresAt` is stamped at inference completion while the heartbeat is timed
 * from the previous CAPTURE, so the TTL must absorb one round trip or a gap
 * reopens on every cycle.
 */
const VISION_TTL_MARGIN_MS = 60_000;

/**
 * A shared surface at 45s. Deliberately shorter than the heartbeat floor: a
 * screen share's picture really does change, so a stale description is worse
 * than none. Its own content churn keeps it refreshed far below this anyway.
 */
export const PUBLIC_SURFACE_OBSERVATION_TTL_MS = 45_000;

/** Longer than signaling's 90s age limit, so stale samples remain diagnosable. */
export const SCOREBOARD_OBSERVATION_TTL_MS = 120_000;

/**
 * A participant camera must survive its own worst-case re-upload interval.
 * Staleness here is benign — a person at a desk in the same room is still there
 * five minutes later — and the context block states `Last observed: Xs ago`, so
 * the companion can hedge on its own.
 */
export const PARTICIPANT_OBSERVATION_TTL_MS =
  VISION_HEARTBEAT_FLOOR_MS + VISION_TTL_MARGIN_MS;

/**
 * A webcam is a near-static scene: one person, one background, small movements.
 * At the shared-surface threshold of 6 a person shifting in their chair or
 * changing clothes reads as an identical frame. 3 catches real changes while
 * still rejecting sensor noise.
 *
 * Affordable because it spends the ambient budget, not the heartbeat one: a
 * real session used 62 of 720 ambient calls per room-hour, while heartbeat is
 * capped at 12 and already shared between sources. Tightening this threshold is
 * why the heartbeat interval itself must NOT be shortened.
 */
export const PARTICIPANT_VISION_SCENE_CHANGE_DISTANCE = 3;

/** Resolve how long a stored observation stays readable, by what produced it. */
export function visionObservationTtlMs(
  sourceKind: VisionSourceKind | undefined,
  promptReason?: string,
): number {
  if (promptReason === 'cfb_scoreboard') return SCOREBOARD_OBSERVATION_TTL_MS;
  return sourceKind === 'participant_video'
    ? PARTICIPANT_OBSERVATION_TTL_MS
    : PUBLIC_SURFACE_OBSERVATION_TTL_MS;
}

const isNonnegativeTime = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER
);

const isIdentifier = (value: unknown): value is string => (
  typeof value === 'string' && /^[a-zA-Z0-9._:-]{1,160}$/.test(value)
);

/** Missing metadata is allowed for older clients; malformed metadata is not. */
export function parseVisionCaptureEvidence(
  value: unknown,
  frameCount: number,
  capturedAt: number,
): VisionCaptureEvidence | null | undefined {
  if (value === undefined) return undefined;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (
    !isIdentifier(record.sourceEpoch) || !isIdentifier(record.clockId) || !isIdentifier(record.frameId) ||
    record.clockDomain !== 'browser_video_element' ||
    !Number.isSafeInteger(record.frameSequence) || record.frameSequence !== frameCount ||
    !isNonnegativeTime(record.capturedAtMs) || record.capturedAtMs !== capturedAt ||
    !isNonnegativeTime(record.captureMonotonicMs) ||
    !isNonnegativeTime(record.captureDurationMs) || record.captureDurationMs > 60_000 ||
    (record.mediaTimeMs !== null && !isNonnegativeTime(record.mediaTimeMs)) ||
    (record.uncertaintyMs !== null && (!isNonnegativeTime(record.uncertaintyMs) || record.uncertaintyMs > 60_000))
  ) return null;

  // Only allow-listed capture fields survive. Client-supplied receive time or
  // a claimed program PTS cannot become server-authoritative evidence.
  return {
    sourceEpoch: record.sourceEpoch,
    clockId: record.clockId,
    clockDomain: 'browser_video_element',
    frameId: record.frameId,
    frameSequence: frameCount,
    capturedAtMs: record.capturedAtMs,
    captureMonotonicMs: record.captureMonotonicMs,
    captureDurationMs: record.captureDurationMs,
    mediaTimeMs: record.mediaTimeMs,
    uncertaintyMs: record.uncertaintyMs,
  };
}


/** Capture freshness is separate from provider completion and viewer alignment. */
export function usableVisualObservation(observation: VisualObservation, now: number): boolean {
  if (observation.failureReason || observation.captureTimestampValid === false
    || !Number.isFinite(observation.observedAt) || observation.observedAt <= 0
    || observation.observedAt > now + 1_000 || !Number.isFinite(observation.expiresAt)
    || observation.expiresAt <= now || observation.observedAt + visionObservationTtlMs(observation.sourceKind, observation.promptReason) <= now
    || !Number.isFinite(observation.confidence) || observation.confidence <= 0 || observation.confidence > 1) return false;
  if (observation.capture !== undefined && parseVisionCaptureEvidence(
    observation.capture, observation.frameCount ?? 0, observation.observedAt) == null) return false;
  return true;
}
